import { useEffect, useRef, useState } from "react";
import { Sparkles, X } from "lucide-react";
import { API } from "../../lib/catalogueApi";
import { openAssistant, visitorId } from "../../lib/assistantApi";

const DELAY = 10000;

// T71: "Ask Rhyte AI" prompt on the PDP. The slot is reserved from first paint (no layout shift); after 10 s of VISIBLE time
// the 3 product questions fade in. Shown once per product per session; dismissible.
export function AskPrompt({ p }) {
  const key = `rst_askp_${p.slug}`;
  const [qs, setQs] = useState(null);
  const [done, setDone] = useState(() => sessionStorage.getItem(key) === "1");
  const t = useRef({ acc: 0, since: null, timer: null });
  useEffect(() => {
    if (done) return undefined;
    const s = t.current;
    const fire = () => {
      sessionStorage.setItem(key, "1");
      fetch(`${API}/assistant/questions/${encodeURIComponent(p.slug)}?visitor_id=${encodeURIComponent(visitorId())}`).then((r) => r.json())
        .then((d) => setQs((d.questions || []).slice(0, 3))).catch(() => setQs([]));
    };
    const arm = () => { s.since = Date.now(); s.timer = setTimeout(fire, Math.max(0, DELAY - s.acc)); };
    const pause = () => { clearTimeout(s.timer); if (s.since) s.acc += Date.now() - s.since; s.since = null; };
    const vis = () => (document.hidden ? pause() : arm());
    if (!document.hidden) arm();
    document.addEventListener("visibilitychange", vis);
    return () => { pause(); document.removeEventListener("visibilitychange", vis); };
  }, [done, key, p.slug]);
  const ask = (q) => openAssistant({ page: "product", product_slug: p.slug, send: q });
  const shown = !done && qs?.length > 0;
  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50/70 p-3" data-testid="pdp-ask-prompt" data-state={shown ? "shown" : "idle"}>
      <div className="flex items-center gap-2">
        <Sparkles size={16} className="text-[var(--brand-primary)]" aria-hidden="true" />
        <p className="flex-1 text-sm text-slate-600">Not sure about something? <button type="button" className="font-bold text-[var(--brand-primary)] underline-offset-2 hover:underline" onClick={() => openAssistant({ page: "product", product_slug: p.slug })} data-testid="pdp-ask-open">Ask Rhyte AI</button></p>
        <button type="button" aria-label="Dismiss suggested questions" onClick={() => { setDone(true); }} data-testid="pdp-ask-dismiss" tabIndex={shown ? 0 : -1} aria-hidden={!shown}
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-500 hover:bg-white ${shown ? "" : "invisible"}`}><X size={16} /></button>
      </div>
      <div className={`mt-1 h-[8.25rem] space-y-1.5 transition-opacity duration-500 motion-reduce:transition-none ${shown ? "opacity-100" : "pointer-events-none opacity-0"}`} aria-hidden={!shown} data-testid="pdp-ask-slot">
        {shown && qs.map((q, i) => (
          <button key={q} type="button" onClick={() => ask(q)} data-testid={`pdp-ask-q-${i}`}
            className="flex h-10 w-full items-center rounded-lg border border-slate-200 bg-white px-3 text-left text-sm text-slate-700 hover:border-[var(--brand-primary)]">
            <span className="truncate">{q}</span></button>
        ))}
      </div>
    </div>
  );
}
